"use client"

import { Menu } from "@base-ui/react/menu"
import { authIdentity } from "@defuse-protocol/internal-utils"
import {
  ArrowRightStartOnRectangleIcon,
  BookOpenIcon,
  ChevronUpIcon,
  Cog8ToothIcon,
  DocumentTextIcon,
  QrCodeIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/16/solid"
import { UserIcon } from "@heroicons/react/24/solid"
import { getAccountLabel } from "@src/app/(app)/(dashboard)/settings/actions"
import { useConnectWallet } from "@src/hooks/useConnectWallet"
import { usePortalContainer } from "@src/hooks/usePortalContainer"
import { GiftIcon, UserPlusIcon } from "@src/icons"
import { useActivityDock } from "@src/providers/ActivityDockProvider"
import { useAddressModal } from "@src/providers/AddressModalProvider"
import {
  useBootstrapActor,
  useBootstrapState,
} from "@src/providers/AppBootstrapProvider"
import { FeatureFlagsContext } from "@src/providers/FeatureFlagsProvider"
import { logger } from "@src/utils/logger"
import { useQuery } from "@tanstack/react-query"
import clsx from "clsx"
import Link from "next/link"
import { Fragment, useContext } from "react"

type MenuItemType = {
  label: string
  onClick?: () => void | Promise<void>
  href?: string
  icon: React.ComponentType<{ className?: string }>
  preventClose?: boolean
  "data-testid"?: string
}

type MenuItemGroupType = MenuItemType[]

const UserMenuDesktop = () => {
  const { clearDockItems } = useActivityDock()
  const { openAddressModal } = useAddressModal()
  const { state, signOut } = useConnectWallet()
  const portalContainer = usePortalContainer()
  const flags = useContext(FeatureFlagsContext)
  const bootstrapActor = useBootstrapActor()
  const bootstrapState = useBootstrapState()
  // Bootstrap exposes "signed_in" (not "ready") once auth + session settle.
  const sessionReady =
    bootstrapState === "signed_in" && state.isAuthorized === true

  const internalAddress =
    state.address != null && state.chainType != null
      ? authIdentity.authHandleToIntentsUserId(state.address, state.chainType)
      : undefined

  const { data: accountLabel } = useQuery({
    queryKey: ["account-label", internalAddress],
    queryFn: async () => {
      const result = await getAccountLabel()
      if (!result.ok) return null
      return result.value
    },
    enabled: internalAddress != null && state.isAuthorized,
  })
  const displayLabel = accountLabel || "Account"

  const itemGroups: MenuItemGroupType[] = [
    [
      {
        label: "Settings",
        href: "/settings",
        icon: Cog8ToothIcon,
      },
      ...(flags.isReferralDisabled
        ? []
        : [
            {
              label: "Referrals",
              href: "/referrals",
              icon: UserPlusIcon,
            },
          ]),
      {
        label: "Gifts",
        href: "/gifts",
        icon: GiftIcon,
      },
    ],
    [
      {
        label: "Support",
        onClick: () => {
          if (typeof window !== "undefined" && window.Beacon) {
            window.Beacon("navigate", "/ask/message/")
            window.Beacon("open")
          }
        },
        icon: QuestionMarkCircleIcon,
      },
      {
        label: "Help articles",
        onClick: () => {
          if (typeof window !== "undefined" && window.Beacon) {
            window.Beacon("navigate", "/answers/")
            window.Beacon("open")
          }
        },
        icon: BookOpenIcon,
      },
      {
        label: "Terms",
        href: "/terms",
        icon: DocumentTextIcon,
      },
    ],
    [
      {
        label: "My address",
        onClick: openAddressModal,
        icon: QrCodeIcon,
        "data-testid": "my-address-menu-item",
      },
      {
        label: "Sign out",
        "data-testid": "sign-out-button",
        onClick: async () => {
          try {
            if (state.chainType) {
              clearDockItems()
              bootstrapActor.send({ type: "SIGN_OUT" })
              await signOut({ id: state.chainType })
            }
          } catch (err) {
            logger.error(err)
          }
        },
        icon: ArrowRightStartOnRectangleIcon,
      },
    ],
  ]

  const getGroupKey = (group: MenuItemGroupType) =>
    group.map((item) => item.label).join("-")

  return (
    <Menu.Root modal={false}>
      <Menu.Trigger
        data-testid="account-indicator"
        data-session-ready={sessionReady ? "true" : "false"}
        className="group relative flex w-full items-center gap-3 rounded-2xl border border-gray-300 confidential:border-transparent bg-gray-50 confidential:bg-gray-900 p-3.5 confidential:text-gray-300 transition-colors duration-200 confidential:hover:bg-gray-950 hover:bg-white confidential:hover:text-white confidential:data-popup-open:bg-gray-950 data-popup-open:bg-white confidential:data-popup-open:text-gray-300 max-xl:hidden"
      >
        <div className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-green-700 confidential:bg-green-500">
          <UserIcon className="size-4.5 confidential:text-black text-white transition-colors duration-200" />
        </div>

        <div className="min-w-0 flex-1 text-left">
          <div className="truncate font-semibold confidential:text-gray-300 text-gray-900 text-sm transition-colors duration-200">
            {displayLabel}
          </div>
        </div>

        <ChevronUpIcon className="size-5 shrink-0 confidential:text-gray-300 text-gray-500 transition-transform duration-100 ease-in-out confidential:group-hover:text-white group-hover:text-gray-700 group-data-popup-open:rotate-180 confidential:group-data-popup-open:text-gray-300 group-data-popup-open:text-gray-700" />
      </Menu.Trigger>

      <Menu.Portal container={portalContainer}>
        <Menu.Positioner
          align="start"
          sideOffset={8}
          className="z-50 outline-hidden"
        >
          <Menu.Popup
            className={clsx(
              "isolate flex min-w-66 flex-col gap-1 rounded-2xl bg-white confidential:bg-gray-950 p-1.5 shadow-[0_-10px_15px_-3px_rgb(0_0_0/0.1),0_-4px_6px_-4px_rgb(0_0_0/0.1)] outline outline-transparent ring-1 ring-gray-900/10 focus:outline-hidden max-xl:hidden",
              "transition-opacity duration-100 ease-out",
              "data-starting-style:opacity-0",
              "data-ending-style:opacity-0 data-ending-style:ease-in"
            )}
            finalFocus={false}
          >
            {itemGroups.map((group, groupIndex) => (
              <Fragment key={`desktop-group-${getGroupKey(group)}`}>
                {group.map((item) => (
                  <MenuItem key={item.label} {...item} />
                ))}
                {groupIndex < itemGroups.length - 1 && (
                  <Menu.Separator className="-mx-1.5 my-0.5 border-gray-200 confidential:border-white/10 border-t" />
                )}
              </Fragment>
            ))}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  )
}

export default UserMenuDesktop

const MenuItem = ({
  href,
  onClick,
  icon: Icon,
  label,
  preventClose,
  "data-testid": dataTestId,
}: MenuItemType) => {
  const classes =
    "group flex items-center gap-2 rounded-xl p-2.5 text-left font-semibold confidential:text-gray-300 text-gray-700 text-sm confidential:hover:bg-gray-800 hover:bg-gray-200 confidential:hover:text-white hover:text-gray-900 focus:outline-hidden confidential:focus-visible:bg-gray-800 focus-visible:bg-gray-200 confidential:focus-visible:text-white focus-visible:text-gray-900"

  const content = (
    <>
      <Icon className="size-4 text-gray-500 confidential:group-hover:text-gray-400 group-hover:text-gray-600 confidential:group-focus:text-gray-400 group-focus:text-gray-600" />
      <span>{label}</span>
    </>
  )

  if (href) {
    return (
      <Menu.LinkItem
        closeOnClick
        render={
          <Link href={href} className={classes} data-testid={dataTestId} />
        }
      >
        {content}
      </Menu.LinkItem>
    )
  }

  return (
    <Menu.Item
      closeOnClick={!preventClose}
      onClick={onClick}
      render={
        <button type="button" className={classes} data-testid={dataTestId} />
      }
      nativeButton
    >
      {content}
    </Menu.Item>
  )
}
